import { readPref, writePref } from './prefs';

/**
 * Option joueur « Garder l'écran allumé » — DECISIONS A60 :
 * « Faire en sorte que l'écran ne tombe pas en veille, activable dans la
 * config. »
 *
 * Screen Wake Lock API (`navigator.wakeLock.request('screen')`). Comme le
 * « Lancer automatique » (`autoThrow.ts`), c'est un confort d'interface :
 * **persisté dans le navigateur**, pas dans la sauvegarde, **désactivé par
 * défaut**. Une fois coché, il vaut pour tous les écrans (combat compris) :
 * le routeur (`app.ts`) l'applique au démarrage, l'Accalmie le règle.
 *
 * Deux couches :
 *  - `createWakeLockController(env)` : petite machine d'état **pure**, sans
 *    DOM, nourrie d'un environnement injecté (l'API, la visibilité) — c'est
 *    elle que testent `tests/ui/wakeLock.test.ts` ;
 *  - l'adaptateur navigateur (`applyWakeLock`, `setWakeLock`,
 *    `onWakeLockChange`) : une seule instance partagée, branchée sur
 *    `visibilitychange`.
 *
 * Règle : le verrou est demandé quand l'option est active **et** l'onglet
 * visible ; il est libéré quand l'option se désactive ou que l'onglet se
 * cache (le navigateur le libère de toute façon), et redemandé au retour de
 * visibilité. API absente (Firefox < 126, Safari < 16.4, page non sécurisée),
 * refus du navigateur (économie d'énergie, politique de permissions) : aucun
 * crash, aucune erreur console, état « indisponible » exposé.
 */

export const WAKE_LOCK_KEY = 'diceordie.wakeLock';

/** Désactivé par défaut (A60). */
export function readWakeLock(): boolean {
  return readPref(WAKE_LOCK_KEY) === '1';
}

export function writeWakeLock(on: boolean): void {
  writePref(WAKE_LOCK_KEY, on ? '1' : '0');
}

/* ── Machine d'état pure ─────────────────────────────────────────────── */

/** Ce que l'on utilise d'un `WakeLockSentinel`. */
export interface WakeLockSentinelLike {
  readonly released?: boolean;
  release(): Promise<void> | void;
  addEventListener?(type: 'release', listener: () => void): void;
}

/** Ce que l'on utilise de `navigator.wakeLock`. */
export interface WakeLockApiLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

export interface WakeLockEnv {
  /** `navigator.wakeLock`, ou `null` quand l'API manque. */
  api: WakeLockApiLike | null;
  /** L'onglet est-il visible (`document.visibilityState !== 'hidden'`) ? */
  isVisible(): boolean;
}

/**
 * - `off` : option désactivée ;
 * - `waiting` : option active mais rien de tenu (onglet caché, ou verrou
 *   relâché par le système) — redemandé au retour de visibilité ;
 * - `requesting` : demande en cours ;
 * - `active` : l'écran est tenu allumé ;
 * - `unavailable` : API absente (`reason: 'unsupported'`) ou dernière
 *   demande refusée (`reason: 'rejected'`, retentée au prochain retour de
 *   visibilité ou à la prochaine activation).
 */
export type WakeLockStatus = 'off' | 'waiting' | 'requesting' | 'active' | 'unavailable';
export type WakeLockUnavailableReason = 'unsupported' | 'rejected';

export interface WakeLockSnapshot {
  enabled: boolean;
  /** L'API existe-t-elle dans ce navigateur ? */
  supported: boolean;
  status: WakeLockStatus;
  reason: WakeLockUnavailableReason | null;
}

export interface WakeLockController {
  /** Active ou désactive l'option (demande ou libère selon la visibilité). */
  setEnabled(on: boolean): void;
  /** À appeler sur `visibilitychange` : relit la visibilité et se met à jour. */
  refresh(): void;
  snapshot(): WakeLockSnapshot;
  /** Prévenu à chaque changement d'état (renvoie le désabonnement). */
  subscribe(fn: (s: WakeLockSnapshot) => void): () => void;
  /** Libère tout et ignore les réponses encore en vol. */
  dispose(): void;
}

function safeRelease(sentinel: WakeLockSentinelLike): void {
  try {
    void Promise.resolve(sentinel.release()).catch(() => undefined);
  } catch {
    /* déjà libéré ou objet hostile : rien à faire */
  }
}

export function createWakeLockController(env: WakeLockEnv): WakeLockController {
  let enabled = false;
  let sentinel: WakeLockSentinelLike | null = null;
  let pending = false;
  let rejected = false;
  let disposed = false;
  const listeners = new Set<(s: WakeLockSnapshot) => void>();
  let lastKey = '';

  function visible(): boolean {
    try {
      return env.isVisible();
    } catch {
      return true;
    }
  }

  function snapshot(): WakeLockSnapshot {
    const supported = env.api !== null;
    let status: WakeLockStatus;
    let reason: WakeLockUnavailableReason | null = null;
    if (!enabled) status = 'off';
    else if (!supported) {
      status = 'unavailable';
      reason = 'unsupported';
    } else if (sentinel) status = 'active';
    else if (pending) status = 'requesting';
    else if (rejected) {
      status = 'unavailable';
      reason = 'rejected';
    } else status = 'waiting';
    return { enabled, supported, status, reason };
  }

  function notify(): void {
    const s = snapshot();
    const key = `${s.enabled}|${s.supported}|${s.status}|${s.reason ?? ''}`;
    if (key === lastKey) return;
    lastKey = key;
    for (const fn of [...listeners]) {
      try {
        fn(s);
      } catch {
        /* un abonné défaillant ne bloque pas les autres */
      }
    }
  }

  function wanted(): boolean {
    return !disposed && enabled && env.api !== null && visible();
  }

  function request(api: WakeLockApiLike): void {
    pending = true;
    rejected = false;
    let p: Promise<WakeLockSentinelLike>;
    try {
      p = Promise.resolve(api.request('screen'));
    } catch {
      pending = false;
      rejected = true;
      return;
    }
    p.then(
      (s) => {
        pending = false;
        // L'option a été décochée, ou l'onglet caché, pendant la demande.
        if (!wanted() || !s || s.released === true) {
          if (s) safeRelease(s);
          // Encore voulu (verrou mort-né) : on attendra le prochain retour.
          notify();
          return;
        }
        sentinel = s;
        try {
          // Libéré par le système (onglet caché, économie d'énergie…).
          s.addEventListener?.('release', () => {
            if (sentinel !== s) return;
            sentinel = null;
            notify();
          });
        } catch {
          /* pas d'événement : la visibilité suffira à resynchroniser */
        }
        notify();
      },
      () => {
        pending = false;
        rejected = true;
        notify();
      },
    );
  }

  function sync(): void {
    if (disposed) return;
    if (wanted()) {
      if (!sentinel && !pending && env.api) request(env.api);
    } else if (sentinel) {
      const s = sentinel;
      sentinel = null;
      safeRelease(s);
    }
    notify();
  }

  lastKey = (() => {
    const s = snapshot();
    return `${s.enabled}|${s.supported}|${s.status}|${s.reason ?? ''}`;
  })();

  return {
    setEnabled(on: boolean): void {
      enabled = on;
      // Une nouvelle activation retente même après un refus.
      if (!on) rejected = false;
      sync();
    },
    refresh(): void {
      sync();
    },
    snapshot,
    subscribe(fn) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    dispose(): void {
      disposed = true;
      if (sentinel) {
        const s = sentinel;
        sentinel = null;
        safeRelease(s);
      }
      listeners.clear();
    },
  };
}

/* ── Adaptateur navigateur ───────────────────────────────────────────── */

/** Lit `navigator.wakeLock` et `document.visibilityState`, sans jamais lever. */
export function browserWakeLockEnv(): WakeLockEnv {
  let api: WakeLockApiLike | null = null;
  try {
    const nav = typeof navigator === 'undefined' ? undefined : (navigator as unknown as { wakeLock?: unknown });
    const wl = nav?.wakeLock as WakeLockApiLike | undefined;
    if (wl && typeof wl.request === 'function') api = wl;
  } catch {
    api = null;
  }
  return {
    api,
    isVisible: () => typeof document === 'undefined' || document.visibilityState !== 'hidden',
  };
}

let shared: WakeLockController | null = null;

/** L'instance partagée par toute l'application (créée à la première demande). */
export function wakeLockController(): WakeLockController {
  if (shared) return shared;
  const controller = createWakeLockController(browserWakeLockEnv());
  shared = controller;
  try {
    document.addEventListener('visibilitychange', () => controller.refresh());
  } catch {
    /* hors navigateur : pas de visibilité à suivre */
  }
  return controller;
}

/** Applique l'option (par défaut, la préférence persistée) à tout l'écran. */
export function applyWakeLock(enabled: boolean = readWakeLock()): WakeLockSnapshot {
  const c = wakeLockController();
  c.setEnabled(enabled);
  return c.snapshot();
}

/** Écrit la préférence et l'applique. */
export function setWakeLock(on: boolean): WakeLockSnapshot {
  writeWakeLock(on);
  return applyWakeLock(on);
}

/** État courant de l'option (pour l'affichage). */
export function wakeLockSnapshot(): WakeLockSnapshot {
  return wakeLockController().snapshot();
}

/** S'abonne aux changements d'état (renvoie le désabonnement). */
export function onWakeLockChange(fn: (s: WakeLockSnapshot) => void): () => void {
  return wakeLockController().subscribe(fn);
}
